import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CheckList } from './types';
import { AppLang } from '@shared/services/i18n/types';
import { detectInitialLanguage } from '@shared/services/i18n/detect-language';
import { AppTheme, detectInitialTheme } from '@shared/services/theme';

type Speed = 0.5 | 1 | 1.5 | 2;

interface AppSettingsState {
  primarySidebarCollapsed: boolean;
  soundVolume: number;
  soundSpeed: Speed;
  checklist: CheckList[];
  language: AppLang;
  theme: AppTheme;
}

const initialState: AppSettingsState = {
  primarySidebarCollapsed: false,
  soundVolume: 1,
  soundSpeed: 1,
  checklist: [],
  language: detectInitialLanguage(),
  theme: detectInitialTheme(),
};

const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    setPrimarySidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.primarySidebarCollapsed = action.payload;
    },
    setSoundVolume: (state, action: PayloadAction<number>) => {
      state.soundVolume = action.payload;
    },
    setSoundSpeed: (state, action: PayloadAction<number>) => {
      state.soundSpeed = (action.payload as Speed) || 1;
    },
    setChecklist: (state, action: PayloadAction<CheckList[]>) => {
      state.checklist = action.payload;
    },
    setLanguage: (state, action: PayloadAction<AppLang>) => {
      state.language = action.payload;
    },
    setTheme: (state, action: PayloadAction<AppTheme>) => {
      state.theme = action.payload;
    },
    addChecklist: (state, action: PayloadAction<CheckList>) => {
      state.checklist.push(action.payload);
    },
    updateChecklist: (state, action: PayloadAction<CheckList>) => {
      const index = state.checklist.findIndex(
        (checklist) => checklist.projectId === action.payload.projectId,
      );

      if (index !== -1) {
        state.checklist[index] = action.payload;
      } else {
        state.checklist.push(action.payload);
      }
    },
    removeChecklist: (state, action: PayloadAction<number>) => {
      state.checklist = state.checklist.filter(
        (checklist) => checklist.projectId !== action.payload,
      );
    },
  },
});

export const { reducer: appSettingsReducer, actions: appSettingsActions } = appSettingsSlice;
